import React from 'react';
import styled from 'styled-components';
import { Link } from 'wouter';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { Icon } from '../../concepts/resources/Icon';
import { DropButton } from '../../ui/DropButton';
import { FlexScroller } from '../../ui/Scrollers';
import { Participant } from '../canvas/participants/Participant';
import { usePermissionCheck } from '../common/PermissionElements';
import { Counter } from '../common/Users';
import { useResolve } from '../Container';
import { Flexer } from '../dashboard/elements/LayoutElements';
import { nameSorter, UserModel, WorkspaceModel } from '../dashboard/Models';
import { HangoutLocation } from '../hangouts/HangoutLocation';
import { HangoutUserObserver } from '../hangouts/helpers/HangoutUserObserver';
import { Colors } from './../../ui';
import { AsidePanel } from './AsidePanel';
import { workspaceInviteViaPopup, workspaceRemoveMember } from './WorkspaceCommands';

const ListItemContent = styled.div`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    gap: .5rem;
`;

const ListItemAction = styled.div`
    display: none;
`;

const ListLabel = styled.div`
`;

const ListName = styled.div`
`;

const ListStatus = styled.div`
    font-size: 0.875rem;
    opacity: .75;
`;

const ListItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${Colors.text};

    span {
        position: relative;
        top: -1px;
    }

    & a:hover * {
        text-decoration: underline;
        color: ${Colors.primary};
    }

    &:hover {
        ${ListItemAction} {
            display: block;
        }
    }
`;

const locationAction = (location?: HangoutLocation): [string?, string?] => {
    if (location) {
        const [name, param] = location.path.split(':');
        switch (name) {
            default: return ['online'];
        }
    }
    return [];
};

export const WorkspaceMemberItem = React.memo<{ member: UserModel, location: HangoutLocation }>(({ member, location }) => {

    useObserver(member);
    useObserver(location);

    const [label, link] = locationAction(location);
    const canRemove = usePermissionCheck('ac_team:write');

    const dispatch = useResolve(CommandDispatcher);
    const workspace = useResolve(WorkspaceModel);

    const onRemove = () => {
        dispatch(workspaceRemoveMember, [member.id, workspace.id]);
    };

    return <ListItem>
        <ListItemContent>
            <Counter size={2.5} color={member.color} photo={member.photo} status={!!location ? 'online' : 'offline'}>
                {Participant.toInitials(member.name)}
            </Counter>
            <ListLabel>
                <ListName>{member.name}</ListName>
                {!!link && <Link to={link}><ListStatus>{label}</ListStatus></Link>}
                {!link && !!label && <ListStatus>{label}</ListStatus>}
            </ListLabel>
        </ListItemContent>
        <ListItemAction>
            {canRemove &&
                <Flexer margin="0 .25">
                    <DropButton rounding="full" onClick={onRemove}>
                        <Icon size="1.25" color={Colors.primaryText} src="deqo://icons/hero/x" />
                    </DropButton>
                </Flexer>
            }
        </ListItemAction>
    </ListItem>;
});

export const WorkspaceMembersPanel = () => {

    const dispatch = useResolve(CommandDispatcher);
    const workspace = useObserver(useResolve(WorkspaceModel));
    const members = workspace.members.slice().sort(nameSorter);
    const canInvite = usePermissionCheck('da:write', 'wo:write') && workspace.joined && workspace.type === 'team';

    return <AsidePanel grow>
        <AsidePanel.Heading>
            <span>People</span>
            {canInvite &&
                <DropButton rounding="full" onClick={() => dispatch(workspaceInviteViaPopup)}>
                    <Icon src="deqo://icons/hero/user-add" />
                </DropButton>
            }
        </AsidePanel.Heading>
        <FlexScroller>
            <AsidePanel.Content>
                <Flexer grow flex="column" gap=".5">
                    {members.map(m =>
                        <HangoutUserObserver key={m.id} userId={m.id} render={hu => (
                            <WorkspaceMemberItem member={m} location={hu?.location ?? null} />
                        )} />
                    )}
                </Flexer>
            </AsidePanel.Content>
        </FlexScroller>
    </AsidePanel>;
};
